import api from './api';

// Alert types
export interface Alert {
  id: number;
  company_id: number;
  severity: 'critical' | 'warning' | 'info';
  type: string;
  title: string;
  message: string;
  status: 'active' | 'acknowledged' | 'resolved';
  acknowledged_at?: string;
  acknowledged_by?: number;
  resolved_at?: string;
  resolved_by?: number;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, any>;
}

export interface AlertHistoryParams {
  page?: number;
  limit?: number;
  severity?: string;
  status?: string;
  type?: string;
  company_id?: number;
}

export interface AlertHistoryResponse {
  alerts: Alert[];
  total: number;
  page: number;
  pages: number;
}

export interface GatewayHealth {
  company_id: number;
  company_name: string;
  gateway_id?: number;
  status: 'healthy' | 'warning' | 'critical';
  last_heartbeat?: string;
  offline_duration?: number;
  message?: string;
}

export interface Notification {
  id: number;
  alert_id: number;
  recipient_email: string;
  status: 'sent' | 'failed';
  error_message?: string;
  sent_at: string;
}

export interface NotificationParams {
  page?: number;
  limit?: number;
  alert_id?: number;
  status?: string;
}

// Matches the alert_rules row (one per company per alert_type; only
// 'gateway_offline' exists today). No rule row = the check is skipped.
export interface AlertRule {
  id: string;
  company_id: string | number;
  alert_type: string;
  enabled: boolean;
  threshold_minutes: number;
  cooldown_minutes: number;
  updated_at?: string;
}

export interface UpdateAlertRuleData {
  enabled?: boolean;
  threshold_minutes?: number;
  cooldown_minutes?: number;
}

/**
 * Get alert history
 */
export async function getAlertHistory(params: AlertHistoryParams = {}): Promise<AlertHistoryResponse> {
  const response = await api.get<AlertHistoryResponse>('/alerts/history', { params });
  return response.data;
}

/**
 * Get gateway health for all companies (super admin) or own company
 */
export async function getGatewayHealth(): Promise<GatewayHealth[]> {
  const response = await api.get<GatewayHealth[]>('/alerts/gateway-health');
  return response.data;
}

/**
 * Get a single alert by ID
 */
export async function getAlert(alertId: number): Promise<Alert> {
  const response = await api.get<Alert>(`/alerts/${alertId}`);
  return response.data;
}

/**
 * Acknowledge an alert
 */
export async function acknowledgeAlert(alertId: number): Promise<Alert> {
  const response = await api.post<Alert>(`/alerts/${alertId}/acknowledge`);
  return response.data;
}

/**
 * Resolve an alert
 */
export async function resolveAlert(alertId: number): Promise<Alert> {
  const response = await api.post<Alert>(`/alerts/${alertId}/resolve`);
  return response.data;
}

/**
 * Get notification log
 */
export async function getNotifications(params: NotificationParams = {}): Promise<Notification[]> {
  const response = await api.get<Notification[]>('/alerts/notifications/log', { params });
  return response.data;
}

/**
 * Manually trigger alert check (super admin only)
 */
export async function runAlertCheck(): Promise<{ message: string; checked: number }> {
  const response = await api.post<{ message: string; checked: number }>('/alerts/check');
  return response.data;
}

/**
 * Get alert rules
 */
export async function getAlertRules(): Promise<{ rules: AlertRule[] }> {
  const response = await api.get<{ rules: AlertRule[] }>('/alerts/rules');
  return response.data;
}

/**
 * Update (or create) the company's alert rule
 */
export async function updateAlertRule(companyId: string | number, data: UpdateAlertRuleData): Promise<AlertRule> {
  const response = await api.put<AlertRule>(`/alerts/rules/${companyId}`, data);
  return response.data;
}
